import { Check, ChevronDown, Search, X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";

interface MultiSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  maxDisplayItems?: number;
  allOption?: boolean;
  allOptionLabel?: string;
  allOptionValue?: string;
  showSearch?: boolean;
  searchThreshold?: number;
  style?: React.CSSProperties;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Seleccionar opciones",
  className,
  maxDisplayItems = 2,
  allOption = true,
  allOptionLabel = "Todos",
  allOptionValue = "all",
  showSearch = false,
  searchThreshold = 10,
  style,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");

  // Show "All" if nothing is selected, or if everything is selected
  const isAllSelected =
    allOption && (selected.length === 0 || selected.length === options.length);

  // Determine if search should be shown
  const shouldShowSearch = showSearch || options.length > searchThreshold;

  // Filter options based on search text
  const filteredOptions = React.useMemo(() => {
    if (!searchText) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText]);

  const handleToggleOption = (value: string, e?: React.MouseEvent) => {
    // Prevent dropdown from closing
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // If it's the "all" option
    if (value === allOptionValue) {
      // If all is selected, deselect all, otherwise select all
      if (isAllSelected) {
        onChange([]);
      } else {
        onChange(options.map((opt) => opt.value));
      }
      return;
    }

    // For regular options
    if (selected.includes(value)) {
      // Remove this value
      onChange(selected.filter((v) => v !== value));
    } else {
      // Add this value
      onChange([...selected, value]);
    }
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent dropdown from closing on Enter
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const getDisplayText = () => {
    if (isAllSelected) {
      return allOptionLabel;
    }

    if (selected.length === 0) {
      return placeholder;
    }

    // If more than one item is selected, show "X seleccionados"
    if (selected.length > 1) {
      return `${selected.length} seleccionados`;
    }

    // If only one item is selected, show its label
    const selectedItem = options.find((opt) => opt.value === selected[0]);
    return selectedItem ? selectedItem.label : selected[0];
  };

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!open) {
      setSearchText("");
    }
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "flex min-h-10 w-full justify-between border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          style={style}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <div
              className={cn(
                "flex items-center rounded px-1 py-0.5",
                selected.length > 0
                  ? "bg-secondary/50"
                  : "text-muted-foreground"
              )}
            >
              {getDisplayText()}
              {selected.length > 0 && (
                <X
                  className="ml-1 h-3 w-3 cursor-pointer"
                  onClick={handleClearAll}
                />
              )}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
        align="start"
        style={{
          maxHeight: "400px",
          overflowY: "auto",
        }}
        sideOffset={4}
      >
        <div className="flex flex-col">
          {allOption && (
            <div className="sticky top-0 z-30 bg-background">
              <div
                className={cn(
                  "flex items-center px-2 py-1.5 cursor-pointer hover:bg-accent",
                  isAllSelected && "bg-accent"
                )}
                onClick={(e) => handleToggleOption(allOptionValue, e)}
              >
                <div className="flex h-4 w-4 items-center justify-center rounded-sm border mr-2">
                  {isAllSelected && <Check className="h-3 w-3" />}
                </div>
                {allOptionLabel}
              </div>
              <DropdownMenuSeparator />
            </div>
          )}

          {shouldShowSearch && (
            <div className="sticky top-[37px] z-20 bg-background p-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  className="pl-8 h-9"
                  value={searchText}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchText && (
                  <X
                    className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchText("");
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div className="overflow-y-auto max-h-[300px]">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "flex items-center px-2 py-1.5 cursor-pointer hover:bg-accent",
                    selected.includes(option.value) && "bg-accent"
                  )}
                  onClick={(e) => handleToggleOption(option.value, e)}
                >
                  <div className="flex h-4 w-4 items-center justify-center rounded-sm border mr-2">
                    {selected.includes(option.value) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-2 py-4 text-center text-muted-foreground">
                No se encontraron resultados
              </div>
            )}
          </div>

          {selected.length > 0 && (
            <div className="sticky bottom-0 z-20 bg-background">
              <DropdownMenuSeparator />
              <div className="flex justify-end px-2 py-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-auto py-1 px-2 text-xs"
                >
                  Limpiar selección
                </Button>
              </div>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
